import MomentUtils from '@date-io/moment'
import moment, { Moment } from 'moment'
import React, { FC, ReactElement, useMemo } from 'react'
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from 'akixi-material-ui-pickers'
import { MaterialUiPickersDate } from 'akixi-material-ui-pickers/typings/date'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

export type AdminFiltersOutlinedDateTimeProps = {
    readonly label: string
    /** Local {@code YYYY-MM-DD HH:mm:ss} string from filter state, or empty when unset (legacy ISO is accepted when parsing). */
    readonly value: string
    readonly onChange: (isoOrEmpty: string) => void
    readonly disabled?: boolean
    readonly className?: string
}

/**
 * Outlined keyboard date-time field sized for the admin filters grid so it matches {@code TextInput}
 * outlined filters (label position, font size, borders).
 *
 * @param props - Label, ISO value, change handler, and optional disabled / className.
 * @returns Pickers provider and field wrapped in the same {@code text-input} shell as other admin filters.
 */
export const AdminFiltersOutlinedDateTime: FC<AdminFiltersOutlinedDateTimeProps> = ({
    label,
    value,
    onChange,
    disabled,
    className
}: AdminFiltersOutlinedDateTimeProps): ReactElement => {
    const [userInfo]: UserInfo = useUserInfo()

    const momentValue: OnlyNullable<Moment> = useMemo((): OnlyNullable<Moment> => {
        if (!isStringNotEmpty(value.trim())) {
            return null
        }

        const parsed: Moment = moment(value.trim())

        return parsed.isValid() ? parsed : null
    }, [value])

    const handleChange = (date: Nullable<MaterialUiPickersDate>): void => {
        if (IsVarEmpty(date)) {
            onChange(EMPTY_STRING)

            return
        }

        const m: Moment = moment(date)

        if (!m.isValid()) {
            return
        }

        onChange(m.format('YYYY-MM-DD HH:mm:ss'))
    }

    const format: string = 'YYYY-MM-DD HH:mm:ss'

    return (
        <div
            className={`text-input af-text-filter af-outlined-datetime-filter${
                isVarNotEmpty(className) && className !== EMPTY_STRING ? ` ${className}` : EMPTY_STRING
            }`}
        >
            <MuiPickersUtilsProvider utils={MomentUtils} locale={userInfo?.SessionInfo?.Language}>
                <KeyboardDateTimePicker
                    fullWidth
                    variant='dialog'
                    inputVariant='outlined'
                    label={label}
                    format={format}
                    value={momentValue}
                    onChange={handleChange}
                    disabled={disabled ?? false}
                    ampm={false}
                    minutesStep={1}
                />
            </MuiPickersUtilsProvider>
        </div>
    )
}
